import React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart as LC,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Activity } from "lucide-react";

/* =======================
   Styles
======================= */
import styles from "./LineChart.module.scss";

// Hardcoded JS values for Recharts, mapped to our SCSS variables
const CHART_PRIMARY = "#2563eb"; // $color-primary
const AXIS_COLOR = "#94a3b8"; // $color-slate-400
const GRID_COLOR = "#e2e8f0"; // $color-slate-200
const TEXT_COLOR = "#64748b"; // $color-slate-500

/* =======================
   Custom Tooltip Component
======================= */
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles["admin-line-chart__tooltip"]}>
        <p className={styles["admin-line-chart__tooltip-label"]}>{label}</p>
        <p className={styles["admin-line-chart__tooltip-value"]}>
          <Activity size={18} />
          {payload[0].value.toLocaleString()} Hits
        </p>
      </div>
    );
  }
  return null;
};

/* =======================
   Custom Legend Component
======================= */
const CustomLegend = () => {
  return (
    <div className={styles["admin-line-chart__legend"]}>
      <div className={styles["admin-line-chart__legend-pill"]}>
        <Activity
          size={16}
          className={styles["admin-line-chart__legend-icon"]}
        />
        <span className={styles["admin-line-chart__legend-text"]}>
          API Hits
        </span>
      </div>
    </div>
  );
};

const LineChart = ({ lineGraphData = [] }) => {
  const data = lineGraphData.map((item) => ({
    name: item.month,
    hits: Number(item.api_hits),
  }));

  return (
    <div className={styles["admin-line-chart__wrapper"]}>
      <ResponsiveContainer width="100%" height="100%">
        <LC
          data={data}
          margin={{ top: 10, right: 10, left: -20, bottom: 0 }} // Adjusted to minimize whitespace
        >
          {/* Softer grid lines */}
          <CartesianGrid
            strokeDasharray="4 4"
            stroke={GRID_COLOR}
            vertical={false} // Hide vertical lines for a cleaner look
          />

          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: TEXT_COLOR, fontWeight: 500 }}
            axisLine={{ stroke: AXIS_COLOR, strokeWidth: 1 }}
            tickLine={false}
            dy={10} // Pushes text down slightly
          />

          <YAxis
            tick={{ fontSize: 12, fill: TEXT_COLOR, fontWeight: 500 }}
            axisLine={false} // Hide Y axis line for modern floating look
            tickLine={false}
            dx={-10}
          />

          {/* Connect Custom Tooltip */}
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: GRID_COLOR, strokeWidth: 2 }}
          />

          <Line
            type="monotone"
            dataKey="hits"
            stroke={CHART_PRIMARY}
            strokeWidth={3}
            dot={{
              r: 4,
              fill: "#ffffff",
              stroke: CHART_PRIMARY,
              strokeWidth: 2,
            }}
            activeDot={{
              r: 6,
              fill: CHART_PRIMARY,
              stroke: "rgba(37, 99, 235, 0.2)", // Primary color with opacity for glow
              strokeWidth: 8,
            }}
          />

          {/* Connect Custom Legend */}
          <Legend verticalAlign="bottom" content={<CustomLegend />} />
        </LC>
      </ResponsiveContainer>
    </div>
  );
};

export default LineChart;
